import React, { useEffect, useRef, useState } from "react";
import { Card } from "../ui/card";
import { Camera, Activity, Brain, BarChart3, Bell, Droplets } from "lucide-react";

const features = [
  {
    icon: Camera,
    title: "Computer Vision Analysis",
    description: "Advanced AI analyzes your skin using the Melanin Index, Erythema Index, and Individual Typology Angle to detect early signs of sun damage.",
  },
  {
    icon: Activity,
    title: "Real-Time UV Tracking",
    description: "Integrates live weather data and UV index information to provide accurate, location-based sun exposure monitoring.",
  },
  {
    icon: Brain,
    title: "Personalized Recommendations",
    description: "Receive custom SPF suggestions and safe tanning times based on your skin type, location, and current UV levels.",
  },
  {
    icon: BarChart3,
    title: "Progress Visualization",
    description: "Track your skin health journey with beautiful data visualizations showing changes over time and exposure patterns.",
  },
  {
    icon: Bell,
    title: "Smart Alerts",
    description: "Get instant notifications when excessive exposure is detected or when skin redness levels indicate it's time to seek shade.",
  },
  {
    icon: Droplets,
    title: "Skin Health Insights",
    description: "Understand your skin's response to sun exposure with scientific measurements and actionable health recommendations.",
  }
];

export default function FeaturesSimple() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [visibleItems, setVisibleItems] = useState(new Set());
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardRefs = useRef([]);

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('data-id');
          if (id) {
            setVisibleItems((prev) => new Set(prev).add(id));
          }
        }
      });
    }, observerOptions);

    // Observe heading
    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    // Observe cards
    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={`py-20 md:py-32 px-4 relative overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading with animation */}
        <div 
          ref={headingRef}
          data-id="heading"
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            visibleItems.has('heading')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Why Sunscreened?
          </h2>
          <p className={`text-lg md:text-xl ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Predictable protection. Guaranteed privacy. Real-time insights.
          </p>
        </div>

        {/* Features grid with staggered animations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => {
            const isVisible = visibleItems.has(`card-${index}`);
            return (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                data-id={`card-${index}`}
                className={`transition-all duration-700 ease-out ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-12'
                }`}
                style={{
                  transitionDelay: isVisible ? `${index * 100}ms` : '0ms'
                }}
              >
                <Card 
                  className={`flex flex-col gap-6 rounded-xl border py-6 shadow-sm transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 hover:border-orange-500/50'
                      : 'bg-white border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className={`@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 ${
                    theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'
                  } pb-6`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg transition-all duration-300 ${
                        theme === 'dark' 
                          ? 'bg-orange-500/10 text-orange-400' 
                          : 'bg-orange-100 text-orange-600'
                      }`}>
                        <feature.icon className="w-5 h-5" />
                      </div>
                      <div className={`leading-none font-semibold text-lg ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {feature.title}
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-2">
                    <div className={`text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {feature.description}
                    </div>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

